/**
 * The panel registry — ADR-015 §3: the closed vocabulary of panels. Each row
 * declares its title, the modes that offer it, the registers it serves, its
 * command-palette verbs (Q4, decided: the row carries them — data now, the
 * palette consumes them when it lands), and an INJECTED mount. The registry
 * file imports no panel implementation (§4 — a panel PR adds its own
 * registration line in main.ts); a mount returns a disposable handle and the
 * engine MUST invoke it whenever the panel leaves the visible layout (Codex
 * P2 on the ADR — a hidden panel must not stay subscribed).
 * Depends on: panel-ids, shell-state (types), store (type only).
 * Depended on by: workspace-shell, main (registration site).
 */
import type { PanelId, Register } from './panel-ids.js';
import type { ShellMode } from '../session/shell-state.js';
import type { AppStore } from '../session/store.js';

export interface PanelHandle {
  dispose(): void;
}

export type PanelMount = (container: HTMLElement, store: AppStore) => PanelHandle;

export interface PanelRegistration {
  readonly id: PanelId;
  readonly title: string;
  /** Modes that OFFER this panel — the FSM scopes availability (§13.2). */
  readonly modes: readonly ShellMode[];
  /** Which registers this panel can serve (brief §1.1; per-panel per Q3). */
  readonly registers: readonly Register[];
  /** Palette verbs, as data — consumed by Ctrl+K when it lands (Q4). */
  readonly paletteVerbs: readonly string[];
  readonly mount: PanelMount;
}

export interface PanelRegistry {
  get(id: PanelId): PanelRegistration;
  /** The availability map the workspace parse takes as INJECTED data (§4). */
  availability(): Readonly<Partial<Record<PanelId, readonly ShellMode[]>>>;
}

/** Build the registry. A duplicate id refuses at wiring time, loudly. */
export function createPanelRegistry(registrations: readonly PanelRegistration[]): PanelRegistry {
  const byId = new Map<PanelId, PanelRegistration>();
  for (const registration of registrations) {
    if (byId.has(registration.id)) {
      throw new Error(`panel "${registration.id}" registered twice — the registry is a closed vocabulary, not a list`);
    }
    byId.set(registration.id, registration);
  }
  return {
    get(id) {
      const found = byId.get(id);
      if (found === undefined) {
        throw new Error(`no panel "${id}" registered — registered: ${[...byId.keys()].join(', ') || '(none)'}`);
      }
      return found;
    },
    availability() {
      return Object.fromEntries([...byId.values()].map((r) => [r.id, r.modes]));
    },
  };
}
